import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { AuthContext } from './authTypes';
import type { AuthUser, AuthContextValue } from './authTypes';

const STORAGE_KEY = 'searchx_auth_token';

// Local-dev-only auth bypass. Double-gated: `import.meta.env.DEV` is true ONLY
// under `vite dev` and is always false in a production `vite build`, so even a
// stray VITE_SKIP_AUTH=true baked into a prod bundle cannot skip the login gate.
// When on, the app self-authenticates as DEV_USER (pairs with the backend running
// SEARCHX_AUTH_ENABLED=false). The real Google OAuth path below is untouched.
const DEV_AUTH_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_SKIP_AUTH === 'true';

const DEV_USER: AuthUser = {
  email: 'dev@localhost',
  name: 'Local Dev',
  picture: '',
};

// Google ID token claims we care about.
interface GoogleIdTokenClaims {
  sub: string;
  email: string;
  email_verified: boolean;
  name: string;
  picture: string;
  exp: number;
  hd?: string; // hosted domain (Google Workspace)
}

function isTokenExpired(token: string): boolean {
  try {
    const claims = jwtDecode<GoogleIdTokenClaims>(token);
    // 30s buffer: treat the token as expired 30s before it actually expires
    // to avoid edge-case clock skew and slow-request races.
    return claims.exp * 1000 < Date.now() + 30_000;
  } catch {
    return true;
  }
}

function extractUser(token: string): AuthUser | null {
  try {
    const claims = jwtDecode<GoogleIdTokenClaims>(token);
    if (!claims.email || !claims.email_verified) return null;
    return {
      email: claims.email,
      name: claims.name ?? claims.email,
      picture: claims.picture ?? '',
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    if (DEV_AUTH_BYPASS) return null; // no real token needed in dev bypass
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && !isTokenExpired(stored)) return stored;
    // Clean up expired token
    if (stored) localStorage.removeItem(STORAGE_KEY);
    return null;
  });

  const user = useMemo(() => {
    if (DEV_AUTH_BYPASS) return DEV_USER;
    return token ? extractUser(token) : null;
  }, [token]);

  // Periodically check token expiry (every 60s).
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      if (isTokenExpired(token)) {
        setToken(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [token]);

  const login = useCallback((credential: string): boolean => {
    if (isTokenExpired(credential)) return false;
    const u = extractUser(credential);
    if (!u) return false;
    localStorage.setItem(STORAGE_KEY, credential);
    setToken(credential);
    return true;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
  }, []);

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: !!user,
      login,
      logout,
    }),
    [user, token, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
